/**
 * "Which hub am I looking at?" — `GET /api/hub/identity` (notes/identity/PLAN.md
 * F11) asked of a hub the app found on the network *before* it has any session
 * with it.
 *
 * Discovery (`services/discovery.ts`) answers a name and an address for a hub.
 * The identity route is what turns that into a hub this device can *name*: its
 * `publicId` is the value a sticker's `h=` is checked against
 * (`utils/adoptLink.ts` → `matchAdoptLinkToHub`), and its `name` is what the
 * app prints instead of the mDNS label.
 *
 * What shapes this module, all of it facts about that one route:
 *   - it is **public but LAN-scoped and rate-limited** (60/min per IP), so the
 *     answer is read **once per hub per session** — a scan that re-runs every
 *     30 seconds must not re-ask;
 *   - a hub **too old to serve it** answers 404, and a hub whose identity key
 *     file is unreadable answers 503. Neither is a failure of discovery: the hub
 *     still exists, still answers `/api/info`, and can still be joined (F8
 *     enrollment does not depend on F11), so both are answered here as *values*
 *     and the hub stays on the list;
 *   - the address is the one discovery gave — and then every other address the
 *     hub's own record carries (`HubIdentityTarget.addresses`, card
 *     `t_9cdad513`), so a hub whose first address does not answer still gets to
 *     name itself — and the ladder of attempts is discovery's own
 *     (`probeManualHub`): the reported port over http first, then over https,
 *     then the hub's other doors (the front door `80`, then the legacy raw API
 *     port `16161`).
 *
 * Nothing here decides anything: it fetches and classifies. The reading of the
 * answer is a pure function, the policy is `config/onboarding.ts`.
 */
import { isHubPublicId } from '../utils/hubIdentity';
import { addressProbeBudget } from '../../electron/announceAddresses';
import { resolveHubFetch } from './hubFetch';
import { HUB_FRONT_DOOR_PORT } from '../config/runtime';

/** The route's response (`otto/src/server/routes/hubIdentity.routes.ts`). */
export interface HubIdentityAnswer {
  /** The hub's database id (`settings.hubId`), stable across re-registration. */
  hubId: string;
  /** F9's `publicId`: first 16 hex of `sha256(publicKey)`. */
  publicId: string;
  /** The hub's Ed25519 public key, base64url. */
  publicKey: string;
  /** The hub's own name for itself (`settings.name`). */
  name: string;
  /**
   * Is the hub registered with the cloud (`system_config.cloud_registered`)?
   *
   * **Absent means "not answered", never `false`** (card `t_4d5dbd6b`): the hub
   * omits the two state fields when it cannot read its own state, and reading
   * absence as a value is how an adoptable hub would get hidden or a claimed
   * one would get offered.
   */
  registered?: boolean;
  /**
   * Would `POST /api/hub/adopt` accept a claim right now? The same question the
   * route itself refuses on (`hubAdoptionService.adoptionState()`), so the two
   * cannot disagree. Absent means "not answered" — see `registered`.
   */
  claimable?: boolean;
}

/**
 * What came back, as a state:
 *   - `known`       — a well-formed identity. The hub named itself.
 *   - `unsupported` — no such route here (404/405), or a 200 that is not a hub
 *                     identity at all (a captive portal, an older build's
 *                     catch-all). Same consequence: this hub publishes nothing.
 *   - `unreachable` — nothing answered on any of the attempts.
 *   - `broken`      — the hub answered and refused: not on this network
 *                     (`lan_required`), rate-limited, or its key file is
 *                     unreadable (`identity_unavailable`). The hub is the
 *                     problem, and it is still listed.
 */
export type HubIdentityState = 'known' | 'unsupported' | 'unreachable' | 'broken';

export interface HubIdentityLookup {
  state: HubIdentityState;
  /** The identity, when there was one. */
  identity: HubIdentityAnswer | null;
  /** The hub's own machine code, when it sent one (`identity_unavailable`, …). */
  code: string | null;
  /** The HTTP status of the answer that decided this, when there was one. */
  status: number | null;
  /**
   * Which address answered, when one did (card `t_9cdad513`): the address of
   * the walk — `ip` first, then `target.addresses` — and the rung that carried
   * the answer. Absent when nothing answered anywhere.
   */
  endpoint?: { ip: string; port: number; secure: boolean };
}

export interface HubIdentityTarget {
  ip: string;
  port: number;
  /**
   * The other addresses this hub's own record carries, in the record's order
   * (card `t_9cdad513`; `DiscoveredHub.addresses`). The question below is asked
   * at `ip` first and then at each of these, until one answers — a hub whose
   * first address has drifted, or is one this device cannot route to, still gets
   * to name itself instead of being asked nowhere.
   *
   * Absent or empty is the single-address case, and behaves exactly as before.
   */
  addresses?: string[];
}

export interface HubIdentityLookupOptions {
  /** Bypass the session cache (a "search again" the person asked for). */
  force?: boolean;
  /** Per-attempt budget. Discovery's own probes run in 800-3000 ms. */
  timeoutMs?: number;
  /** Injected by tests; the runtime uses `fetch`. */
  fetchImpl?: typeof fetch;
  /** Injected by tests; a deterministic ladder in place of the three attempts. */
  attempts?: Array<{ scheme: 'http' | 'https'; port: number }>;
}

const DEFAULT_TIMEOUT_MS = 2500;

/** Session cache: one answer per hub per app run. */
const cache = new Map<string, HubIdentityLookup>();
/** An answer on its way, so concurrent discoveries share one request. */
const inFlight = new Map<string, Promise<HubIdentityLookup>>();

function cacheKey(target: HubIdentityTarget): string {
  return `${target.ip}:${target.port}`;
}

/** The already-known answer for a hub, or null. */
export function cachedHubIdentity(ip: string, port: number): HubIdentityLookup | null {
  return cache.get(cacheKey({ ip, port })) ?? null;
}

/** Forget every answer (the app's "clear cache & data", and a fresh scan). */
export function clearHubIdentityCache(): void {
  cache.clear();
  inFlight.clear();
}

/**
 * `probeManualHub`'s ladder, shortest first: the port the hub was found on, both
 * schemes, then the doors a LAN hub also answers on.
 *
 * **Which scheme comes first is a fact about the port** (card `t_c2446418`): the
 * hub's own listener is cleartext — `80` is the front door, `16161` the port it
 * answered on before the door moved — so a hub found on either is dialled over
 * HTTP first, and a TLS ClientHello is never sent to a socket that speaks plain
 * HTTP. `443` is the one port that is TLS by convention (nginx terminating it),
 * so there HTTPS still leads.
 *
 * The rungs behind the named port are the hub's other doors, tried for a hub
 * whose *stored* port has moved: the front door (a hub that upgraded to the new
 * front door is still found by an address that remembers `443` or `16161`), then
 * the raw API port `16161` (bound for one release as a deprecated alias).
 *
 * Shared with `services/hubAdoption.ts` on purpose: a hub that answers
 * `GET /api/hub/identity` on `http:16161` must be adopted on the same rung, and
 * two ladders would drift the first time one of them learned a new address
 * shape.
 */
export interface HubApiAttempt {
  scheme: 'http' | 'https';
  port: number;
}

export function hubApiAttempts(port: number): HubApiAttempt[] {
  const attempts: HubApiAttempt[] =
    port === 443
      ? [
          { scheme: 'https', port },
          { scheme: 'http', port },
        ]
      : [
          { scheme: 'http', port },
          { scheme: 'https', port },
        ];
  if (port !== HUB_FRONT_DOOR_PORT) attempts.push({ scheme: 'http', port: HUB_FRONT_DOOR_PORT });
  if (port !== 16161) attempts.push({ scheme: 'http', port: 16161 });
  return attempts;
}

/**
 * The addresses this question is asked at, in order: **the one the caller named
 * first, then the hub's own list behind it** (card `t_9cdad513`).
 *
 * The caller's address is never dropped and never filtered — it may be a name
 * (`localhost` for a hub on this machine, card `t_118a0a61`) — while the ones
 * behind it come from the record's own ranking. Duplicates are collapsed, and
 * an empty entry says nothing.
 */
function identityAddresses(target: HubIdentityTarget): string[] {
  const ordered: string[] = [];
  for (const value of [target.ip, ...(target.addresses ?? [])]) {
    const host = typeof value === 'string' ? value.trim() : '';
    if (!host || ordered.includes(host)) continue;
    ordered.push(host);
  }
  return ordered;
}

function defaultAttempts(port: number): HubApiAttempt[] {
  return hubApiAttempts(port);
}

/** Turn a response body into an identity, or null when it is not one. */
function readIdentity(body: unknown): HubIdentityAnswer | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as Record<string, unknown>;
  const publicId = typeof raw.publicId === 'string' ? raw.publicId.trim().toLowerCase() : '';
  // The public id is the one field every consumer needs; a body without one is
  // not this route's answer, whatever else it carries.
  if (!isHubPublicId(publicId)) return null;
  return {
    hubId: typeof raw.hubId === 'string' ? raw.hubId : '',
    publicId,
    publicKey: typeof raw.publicKey === 'string' ? raw.publicKey : '',
    name: typeof raw.name === 'string' ? raw.name : '',
    // Only a real boolean is an answer. An omitted key, `null` or a string
    // stays *absent* — the hub says "I cannot read my own state" by leaving it
    // out, and a caller that needs a yes/no decision must treat that as "not
    // answered" rather than as a no.
    ...(typeof raw.registered === 'boolean' ? { registered: raw.registered } : {}),
    ...(typeof raw.claimable === 'boolean' ? { claimable: raw.claimable } : {}),
  };
}

function failureCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const code = (body as Record<string, unknown>).code;
  return typeof code === 'string' && code ? code : null;
}

/**
 * Ask one hub who it is. Never throws: an answer the hub did not give is a state
 * (`unsupported`/`unreachable`/`broken`), because every caller's job is to keep
 * the hub on the list and say what it could not find out.
 */
export async function lookupHubIdentity(
  target: HubIdentityTarget,
  options: HubIdentityLookupOptions = {},
): Promise<HubIdentityLookup> {
  const key = cacheKey(target);
  if (!options.force) {
    const cached = cache.get(key);
    if (cached) return cached;
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const task = (async (): Promise<HubIdentityLookup> => {
    // The app's own hub transport: in Electron this goes through the IPC bridge
    // with the URL as a string, so the port survives Chromium's URL parse and
    // the identity question is asked of the hub that was found (measured on
    // card t_947d6b10 — see `services/hubFetch.ts`).
    const doFetch = options.fetchImpl ?? resolveHubFetch();

    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const attempts = options.attempts ?? defaultAttempts(target.port);
    let lastStatus: number | null = null;
    let lastCode: string | null = null;

    // The question is asked at the address the caller named, and then at every
    // address the hub's own record carries (card `t_9cdad513`): one address that
    // does not answer — a stranded A record, an interface this device cannot
    // route to — is not this hub being unable to name itself. `addressProbeBudget`
    // prices the address behind the first one short, for the same reason the
    // endpoint walk does: a hub on this device's networks answers in milliseconds.
    const addresses = identityAddresses(target);

    for (const [index, address] of addresses.entries()) {
      const budgetMs = addressProbeBudget(index, timeoutMs) ?? timeoutMs;
      for (const attempt of attempts) {
        const endpoint = { ip: address, port: attempt.port, secure: attempt.scheme === 'https' };
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), budgetMs);
        try {
          const response = await doFetch(`${attempt.scheme}://${address}:${attempt.port}/api/hub/identity`, {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
          });
          lastStatus = response.status;

          // A hub without the route: this is the answer, not a reason to try the
          // next scheme — the next scheme is the same hub.
          if (response.status === 404 || response.status === 405) {
            return { state: 'unsupported', identity: null, code: null, status: response.status, endpoint };
          }
          if (!response.ok) {
            const body = await response.json().catch(() => null);
            lastCode = failureCode(body);
            // 403 (lan_required), 429 (rate_limited) and 503
            // (identity_unavailable) are this hub's own refusals: the hub is
            // there and the answer will not change on another port.
            return { state: 'broken', identity: null, code: lastCode, status: response.status, endpoint };
          }

          const body = await response.json().catch(() => null);
          const identity = readIdentity(body);
          if (identity) {
            return { state: 'known', identity, code: null, status: response.status, endpoint };
          }
          // A 200 that is not an identity (a captive portal, an old catch-all):
          // this hub publishes nothing usable.
          return { state: 'unsupported', identity: null, code: null, status: response.status, endpoint };
        } catch {
          // Timeout, refused connection, DNS — try the next address shape.
          continue;
        } finally {
          clearTimeout(timer);
        }
      }
    }

    return { state: 'unreachable', identity: null, code: lastCode, status: lastStatus };
  })().finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, task);
  const result = await task;
  cache.set(key, result);
  return result;
}
